import React from 'react';
import { DeviceControl } from '../DeviceControl/DeviceControl';
import { DimmableLightControl } from '../DeviceControl/DimmableLightControl';
import { CurtainControl } from '../DeviceControl/CurtainControl';
import { CONTROL_KIND } from '../../../lib/rcu/catalog/control';
import { LOAD_ICON_MAP } from '../../../lib/rcu/catalog/icon';
import { LOAD_TYPE_SPECS } from '../../../lib/rcu/catalog/loadType';
import { POWER_STATE } from '../../../lib/device/catalog';
import type { TravelKind } from '../../../lib/rcu/catalog/dinValue';
import type { WiredLoad } from '../../../lib/rcu/model';
import { assertNever } from '../../../lib/exhaustive';

export interface LoadTileProps {
  readonly load: WiredLoad;
  /** The output's wiring address, shown small under the state. Null when unknown. */
  readonly subtext?: string | null;
  /** What to draw: the device's own report where there is one, otherwise the guest's last action. */
  readonly power: boolean;
  readonly level: number;
  readonly onToggle: () => void;
  readonly onLevel: (level: number) => void;
  readonly onTravel: (travel: TravelKind) => void;
}

/**
 * One wired load, drawn with the control its load type asks for.
 *
 * Presentational on purpose: which control a load gets, and what state that
 * control is shown in, is decided by its caller — the device's reports and the
 * guest's own actions are weighed there. That keeps this half renderable on its
 * own, which is how the guest surface is tested without a browser.
 */
export const LoadTile: React.FC<LoadTileProps> = ({
  load,
  subtext,
  power,
  level,
  onToggle,
  onLevel,
  onTravel,
}) => {
  const Icon = LOAD_ICON_MAP[load.iconKey];
  const icon = <Icon className="w-6 h-6" />;
  const status = power ? POWER_STATE.ON : POWER_STATE.OFF;
  const control = LOAD_TYPE_SPECS[load.type].control;

  switch (control) {
    case CONTROL_KIND.TOGGLE:
      return (
        <DeviceControl
          id={load.id}
          name={load.name}
          subtext={subtext ?? undefined}
          icon={icon}
          status={status}
          isOnline
          onToggle={onToggle}
        />
      );

    case CONTROL_KIND.LEVEL:
      return (
        <DimmableLightControl
          id={load.id}
          name={load.name}
          subtext={subtext ?? undefined}
          icon={icon}
          status={status}
          brightness={level}
          isOnline
          onToggle={onToggle}
          onBrightnessChange={(_id, next) => onLevel(next)}
        />
      );

    case CONTROL_KIND.POSITION:
      return (
        <CurtainControl
          id={load.id}
          name={load.name}
          subtext={subtext ?? undefined}
          icon={icon}
          status={status}
          isOnline
          onToggle={onToggle}
          onTravel={(_id, travel) => onTravel(travel)}
        />
      );

    default:
      return assertNever(control);
  }
};

export default LoadTile;
